import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const Contact: React.FC = () => {
  const faqs = [
    {
      q: 'How accurate are your predictions?',
      a: 'Our astrologer, Srinivas Malyala Garu, has over 30 years of dedicated practice in traditional Vedic astrology. Clients across India and the USA consistently praise the precision of his predictions. He combines deep scriptural knowledge with practical experience, ensuring readings are grounded in authentic Jyotish Shastra principles — not generic or computer-generated outputs.',
    },
    {
      q: 'What information do I need to provide?',
      a: 'For an accurate Kundali and consultation, we need your exact date of birth, time of birth (as precise as possible), and place of birth. If your exact birth time is unknown, our astrologer uses rectification techniques based on key life events to approximate it. You can also generate a free birth chart on our Kundali page before your consultation to come prepared.',
    },
    {
      q: 'How long does a consultation last?',
      a: 'A typical consultation lasts 1.5 to 2 hours. This includes a detailed review of your Rasi chart, planetary positions, ongoing Mahadasha/Antardasha, and specific questions you may have about career, marriage, health, or finances. Unlike rushed readings, we take the time to explain every aspect so you fully understand the guidance.',
    },
    {
      q: 'Do you provide remedies?',
      a: 'Yes — practical and affordable remedies are a core part of every consultation. These include gemstone recommendations (based on your weak or afflicted planets), specific mantras, charitable activities, dietary suggestions, and lifestyle adjustments. Our astrologer focuses on remedies that are realistic and actionable — not expensive rituals that burden your pocket.',
    },
    {
      q: 'Can consultations be done online?',
      a: 'Absolutely! We offer both in-person visits at our Hyderabad office and online video consultations via secure Jitsi Meet. Many of our clients from the USA, UK, and other countries consult regularly online. After booking and payment, you\'ll receive a private video call link and room code via email — just join at your scheduled time from any device.',
    },
    {
      q: 'What services do you offer?',
      a: 'We offer comprehensive Vedic astrology services including horoscope reading, marriage/compatibility matching (గుణ మిలానం), career and business guidance, Vastu consultation, gemstone analysis, and special muhurtha selection for auspicious events. Each service is personalized based on your unique birth chart — not one-size-fits-all advice.',
    },
    {
      q: 'How do I book and pay?',
      a: 'Booking is simple — visit our Book Consultation page, choose your preferred date and time slot, fill in your details, and pay securely via Razorpay (UPI, cards, net banking accepted). You\'ll receive a confirmation email immediately with all details including the video call link (for online) or location map (for in-person visits).',
    },
  ];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, pt: { xs: 2, md: 3 } }}>
        <Typography variant="h1" sx={{ mb: 2 }}>Get In Touch</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 440, mx: 'auto' }}>
          We'd love to hear from you. Reach out for consultations or any questions.
        </Typography>
      </Box>

      {/* Email & Location — two clean cards side by side */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: { xs: 4, md: 6 } }}>
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <CardContent>
            <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>Email</Typography>
            <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-word' }}>
              amruthakalasamastrology@gmail.com
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href="mailto:amruthakalasamastrology@gmail.com"
            >
              Send Email
            </Button>
          </CardContent>
        </Card>

        <Card sx={{ p: 3, textAlign: 'center' }}>
          <CardContent>
            <LocationIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1.5 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>Location</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Huda Sai Nagar, Kamala Nagar, Hyderabad, Telangana — 500070
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href="https://maps.app.goo.gl/su15tdVwPaXePGEM6"
              target="_blank"
            >
              View on Map
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Consultation Hours */}
      <Card sx={{ mb: { xs: 4, md: 6 }, p: { xs: 2, md: 3 } }}>
        <CardContent>
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 3 }}>
            <ScheduleIcon sx={{ fontSize: 26, mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
            Consultation Hours
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            {[
              { day: 'Mon – Fri', time: '10 AM – 8 PM' },
              { day: 'Saturday', time: '10 AM – 6 PM' },
              { day: 'Sunday', time: '11 AM – 5 PM' },
            ].map((s) => (
              <Box key={s.day} sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#FFF5EB', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{s.day}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'secondary.main' }}>{s.time}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
            All times in IST · Emergency consultations on prior appointment
          </Typography>
        </CardContent>
      </Card>

      {/* FAQs */}
      <Box sx={{ mb: { xs: 4, md: 6 } }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 3 }}>
          Frequently Asked Questions
        </Typography>
        {faqs.map((f, i) => (
          <Card key={i} sx={{ mb: 1.5, p: { xs: 1.5, md: 2 } }}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontSize: '0.9rem', mb: 0.5 }}>
              {f.q}
            </Typography>
            <Typography variant="body2">{f.a}</Typography>
          </Card>
        ))}
      </Box>

      {/* CTA */}
      <Card sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', backgroundColor: '#FFF5EB' }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 1.5 }}>Ready for Your Consultation?</Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Book a slot and take the first step towards clarity.
          </Typography>
          <Button variant="contained" size="large" href="/book-slot">
            Book Consultation
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Contact;
